import React, { useState, useEffect } from 'react';
import './ProofBuilder.css';
import { useProgress } from '../contexts/ProgressContext';

const PROOF_COMPONENTS = [
  { id: 'statement', label: 'Statement', description: 'What you want to prove', color: '#60a5fa' },
  { id: 'commitment', label: 'Commitment', description: 'A coded hint', color: '#10b981' },
  { id: 'randomizer', label: 'Randomizer', description: 'Extra privacy layer', color: '#a78bfa' },
  { id: 'witness', label: 'Witness', description: 'Your secret (stays hidden)', color: '#f59e0b' },
  { id: 'challenge', label: 'Challenge', description: 'Verifier\'s test', color: '#ec4899' }
];

const LEVELS = [
  {
    id: 1,
    title: 'Basic Proof Structure',
    instruction: 'Build a simple proof: You need a Statement and a Commitment to start',
    requiredSlots: ['statement', 'commitment'],
    explanation: 'Every zero-knowledge proof begins with a statement (what you claim) and a commitment (a cryptographic promise). The commitment is like sealing your answer in an envelope—the verifier knows you made a claim, but can\'t see inside!',
    realWorld: 'Like showing a sealed envelope to prove you wrote something down, without opening it.'
  },
  {
    id: 2,
    title: 'Adding Privacy',
    instruction: 'Make your proof more secure: Add a Randomizer for extra privacy',
    requiredSlots: ['statement', 'commitment', 'randomizer'],
    explanation: 'The randomizer adds unpredictability, making it impossible for anyone to reverse-engineer your secret from the commitment. It\'s like shuffling cards before dealing—adds randomness that protects your information.',
    realWorld: 'Like adding static to a radio signal so eavesdroppers can\'t decode your message, but the intended recipient still can.'
  },
  {
    id: 3,
    title: 'Complete ZK Proof',
    instruction: 'Build a full proof: Statement, Commitment, Witness (hidden), Randomizer, and Challenge',
    requiredSlots: ['statement', 'witness', 'commitment', 'randomizer', 'challenge'],
    explanation: 'A complete zero-knowledge proof! The witness (your secret) stays with you and never gets revealed. The challenge ensures you actually know the secret—the verifier tests you with random questions. If you pass, you\'ve proven knowledge without sharing it.',
    realWorld: 'Like proving you know the password to an account by logging in successfully, without ever typing the password where others can see it.'
  }
];

const ProofBuilder = () => {
  const { updateGameProgress, getProgress } = useProgress();
  const [currentLevel, setCurrentLevel] = useState(1);
  const [placedComponents, setPlacedComponents] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [showExplanation, setShowExplanation] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [showGlossary, setShowGlossary] = useState(null);

  const level = LEVELS[currentLevel - 1];

  useEffect(() => {
    const saved = getProgress('games', 'proof-builder');
    if (saved && typeof saved.level === 'number') {
      // level represents completed levels, so currentLevel should be the next level to play
      const completedLevels = saved.level;
      const nextLevel = Math.min(completedLevels + 1, LEVELS.length);
      setCurrentLevel(nextLevel);
    } else {
      updateGameProgress('proof-builder', null, 0, false);
    }
  }, [getProgress, updateGameProgress]);

  const handleDragStart = (e, component) => {
    setDraggedItem(component);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedItem && !placedComponents.find(c => c.id === draggedItem.id)) {
      setPlacedComponents([...placedComponents, draggedItem]);
      setDraggedItem(null);
      setFeedback('');
      setIsCorrect(false);
    }
  };

  const handleRemove = (componentId) => {
    setPlacedComponents(placedComponents.filter(c => c.id !== componentId));
    setFeedback('');
    setIsCorrect(false);
  };

  const checkProof = () => {
    const placedIds = placedComponents.map(c => c.id).sort();
    const requiredIds = level.requiredSlots.sort();
    
    const isValid = requiredIds.every(id => placedIds.includes(id)) && 
                    placedIds.length === requiredIds.length;

    setIsCorrect(isValid);
    if (isValid) {
      setFeedback('Perfect! Your proof structure is valid!');
      setShowExplanation(true);
    } else {
      const missing = requiredIds.filter(id => !placedIds.includes(id));
      const extra = placedIds.filter(id => !requiredIds.includes(id));
      
      let msg = 'Not quite right. ';
      if (missing.length > 0) {
        msg += `You're missing: ${missing.map(id => PROOF_COMPONENTS.find(c => c.id === id).label).join(', ')}. `;
      }
      if (extra.length > 0) {
        msg += `Remove: ${extra.map(id => PROOF_COMPONENTS.find(c => c.id === id).label).join(', ')}.`;
      }
      setFeedback(msg);
      setShowExplanation(false);
    }
  };

  const nextLevel = () => {
    if (currentLevel < LEVELS.length) {
      // Mark current level as completed before moving to next
      updateGameProgress('proof-builder', null, currentLevel, false);
      const newLevel = currentLevel + 1;
      setCurrentLevel(newLevel);
      setPlacedComponents([]);
      setFeedback('');
      setIsCorrect(false);
      setShowExplanation(false);
    } else {
      // Mark final level as completed
      updateGameProgress('proof-builder', null, LEVELS.length, true, 'Private Proofs');
    }
  };

  return (
    <div className="proof-builder">
      {showIntro && (
        <div className="pb-modal-overlay">
          <div className="pb-modal">
            <div className="pb-modal-header">
              <h2>Build Your Own Zero-Knowledge Proof!</h2>
              <button className="pb-close" onClick={() => setShowIntro(false)}>×</button>
            </div>
            <div className="pb-modal-body">
              <p>
                Drag items to construct a proof that convinces the system you know a secret, 
                while keeping it hidden. This simulates how real ZKPs work in blockchains and privacy tech.
              </p>
              <div className="pb-intro-features">
                <div className="pb-feature">
                  <h4>Learn By Building</h4>
                  <p>Assemble proof components in the correct order to see how ZK proofs are structured.</p>
                </div>
                <div className="pb-feature">
                  <h4>Understand Privacy</h4>
                  <p>Discover how commitments, randomizers, and challenges protect your secrets.</p>
                </div>
                <div className="pb-feature">
                  <h4>Interactive Learning</h4>
                  <p>Simple drag-and-drop interface—learn by doing!</p>
                </div>
              </div>
            </div>
            <div className="pb-modal-footer">
              <button className="pb-primary" onClick={() => setShowIntro(false)}>Start Building</button>
            </div>
          </div>
        </div>
      )}

      <div className="pb-header">
        <h1>Proof Builder</h1>
        <div className="pb-level-indicator">
          Level {currentLevel} of {LEVELS.length}
        </div>
      </div>

      <div className="pb-instruction">
        <h2>{level.title}</h2>
        <p>{level.instruction}</p>
      </div>

      <div className="pb-game-area">
        <div className="pb-components-panel">
          <h3>Available Components</h3>
          <div className="pb-components-list">
            {PROOF_COMPONENTS.map(component => (
              <div
                key={component.id}
                className={`pb-component ${placedComponents.find(c => c.id === component.id) ? 'placed' : ''}`}
                draggable={!placedComponents.find(c => c.id === component.id)}
                onDragStart={(e) => handleDragStart(e, component)}
                style={{ borderColor: component.color }}
                onMouseEnter={() => setShowGlossary(component)}
                onMouseLeave={() => setShowGlossary(null)}
              >
                <div className="pb-component-icon" style={{ backgroundColor: `${component.color}20` }}>
                  <div style={{ color: component.color, fontSize: '20px', fontWeight: 'bold' }}>
                    {component.label[0]}
                  </div>
                </div>
                <div className="pb-component-label">{component.label}</div>
              </div>
            ))}
          </div>
          
          {showGlossary && (
            <div className="pb-glossary-tooltip">
              <strong>{showGlossary.label}</strong>
              <p>{showGlossary.description}</p>
            </div>
          )}
        </div>

        <div className="pb-proof-area">
          <h3>Your Proof Structure</h3>
          <div
            className={`pb-drop-zone ${placedComponents.length > 0 ? 'has-items' : ''}`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {placedComponents.length === 0 ? (
              <div className="pb-drop-hint">Drag components here to build your proof</div>
            ) : (
              <div className="pb-placed-components">
                {placedComponents.map((component, index) => (
                  <div
                    key={component.id}
                    className="pb-placed-component"
                    style={{ borderColor: component.color }}
                  >
                    <div className="pb-placed-icon" style={{ backgroundColor: `${component.color}20`, color: component.color }}>
                      {component.label[0]}
                    </div>
                    <div className="pb-placed-info">
                      <div className="pb-placed-label">{component.label}</div>
                      <div className="pb-placed-desc">{component.description}</div>
                    </div>
                    <button
                      className="pb-remove-btn"
                      onClick={() => handleRemove(component.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pb-actions">
            <button
              className="pb-check-btn"
              onClick={checkProof}
              disabled={placedComponents.length === 0}
            >
              Verify Proof Structure
            </button>
            <button
              className="pb-reset-btn"
              onClick={() => {
                setPlacedComponents([]);
                setFeedback('');
                setIsCorrect(false);
                setShowExplanation(false);
              }}
            >
              Reset
            </button>
          </div>

          {feedback && (
            <div className={`pb-feedback ${isCorrect ? 'success' : 'error'}`}>
              {feedback}
            </div>
          )}
        </div>
      </div>

      {showExplanation && isCorrect && (
        <div className="pb-explanation">
          <h3>What You Just Built</h3>
          <div className="pb-explanation-content">
            <div className="pb-explanation-section">
              <h4>How It Works</h4>
              <p>{level.explanation}</p>
            </div>
            <div className="pb-explanation-section">
              <h4>Real-World Example</h4>
              <p>{level.realWorld}</p>
            </div>
          </div>
          {currentLevel < LEVELS.length ? (
            <button className="pb-next-btn" onClick={nextLevel}>
              Next Level
            </button>
          ) : (
            <div className="pb-completion">
              <h3>Congratulations!</h3>
              <p>
                You've constructed complete zero-knowledge proofs! These components—statements, 
                commitments, randomness—work together to satisfy verifiers without exposing secrets. 
                Now you understand how ZKPs protect users' data in real blockchain applications!
              </p>
              <button className="pb-next-btn" onClick={() => setCurrentLevel(1)}>
                Play Again
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProofBuilder;

